/**
 * Errors that reach a reader.
 *
 * Every backend error message is an i18n KEY rather than prose — the backend
 * cannot know which language the eventual reader speaks. This carries the key
 * and any details through to the toast, which translates it.
 *
 * A raw Firebase code (`functions/permission-denied`) must never reach a
 * teacher. It tells them nothing, and it looks like the product broke.
 */
export class AppError extends Error {
  readonly key: string;
  readonly params: Record<string, unknown>;
  readonly code: string;

  constructor(key: string, code = "unknown", params: Record<string, unknown> = {}) {
    super(key);
    this.name = "AppError";
    this.key = key;
    this.code = code;
    this.params = params;
  }
}

/** A message that looks like an i18n key: dotted, no spaces. */
export function looksLikeKey(message: string): boolean {
  return /^[a-z][A-Za-z0-9]*(\.[A-Za-z0-9]+)+$/.test(message);
}

/**
 * Normalises anything thrown by a callable into an `AppError`.
 *
 * Firebase prefixes a callable's code, so `permission-denied` arrives as
 * `functions/permission-denied`. The message is our i18n key when the backend
 * raised it deliberately, and something else entirely when the network failed.
 */
export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  const raw = error as { code?: string; message?: string; details?: unknown };
  const code = typeof raw?.code === "string" ? raw.code.replace(/^functions\//, "") : "unknown";
  const message = typeof raw?.message === "string" ? raw.message : "";

  const params =
    raw?.details && typeof raw.details === "object"
      ? (raw.details as Record<string, unknown>)
      : {};

  if (looksLikeKey(message)) return new AppError(message, code, params);

  // Not one of ours. Map the transport-level cases to something a person can
  // act on, and fall back to a generic key rather than showing a stack.
  // A session that is real but too old for a sensitive operation, such as
  // enrolling a second factor. The account is not in trouble; the credential
  // just has to be proven again.
  if (code === "auth/requires-recent-login") {
    return new AppError("error.auth.staleLogin", code, params);
  }
  if (code === "unauthenticated") return new AppError("error.auth.required", code, params);
  if (code === "permission-denied") return new AppError("error.permission.denied", code, params);
  if (code === "unavailable" || code === "deadline-exceeded") {
    return new AppError("error.network.unavailable", code, params);
  }

  return new AppError("error.unexpected", code, params);
}
